import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import StatusBadge from "@/components/StatusBadge";
import { BirthdaysCard } from "@/components/BirthdaysCard";
import { TopStreaksPreview } from "@/components/StreakCard";
import {
    Users, Receipt, ChatCircleDots, Wallet, UserCheck, ArrowRight, Lightning, Buildings,
} from "@phosphor-icons/react";

function MetricCard({ label, value, icon: Icon, accent, sub, globalScope }) {
    return (
        <Card className="border-[#E2E8F0] an-card-hover" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                    <div className={`h-10 w-10 rounded-md flex items-center justify-center ${accent}`}>
                        <Icon size={20} weight="bold" className="text-white" />
                    </div>
                    {sub && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                            {sub}
                        </span>
                    )}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                    {label}
                </div>
                <div className="text-3xl font-bold text-[#003366] mt-1" style={{ fontFamily: "Outfit" }}>
                    {value}
                </div>
                {globalScope && (
                    <div className="text-[10px] text-slate-400 mt-1.5 uppercase tracking-wider">
                        Across all sites
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [pendingUsers, setPendingUsers] = useState([]);
    const [recentRequests, setRecentRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sites, setSites] = useState([]);
    const [siteFilter, setSiteFilter] = useState("all");

    // Sites list (one-shot)
    useEffect(() => {
        let cancelled = false;
        api.get("/sites").then(({ data }) => {
            if (!cancelled) setSites(Array.isArray(data) ? data : []);
        }).catch(() => { /* non-fatal */ });
        return () => { cancelled = true; };
    }, []);

    // Stats + pending lists (re-fetch when site filter changes)
    useEffect(() => {
        let cancelled = false;
        const params = siteFilter !== "all" ? { site: siteFilter } : {};
        Promise.all([
            api.get("/admin/stats", { params }),
            api.get("/users", { params: { status: "pending" } }),
            api.get("/requests", { params: { status: "pending" } }),
        ])
            .then(([s, u, r]) => {
                if (cancelled) return;
                setStats(s?.data ?? null);
                setPendingUsers(Array.isArray(u?.data) ? u.data : []);
                setRecentRequests(Array.isArray(r?.data) ? r.data : []);
            })
            .catch(() => {
                if (cancelled) return;
                setStats(null);
                setPendingUsers([]);
                setRecentRequests([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [siteFilter]);

    const siteScoped = siteFilter !== "all";
    const activeSites = useMemo(() => sites.filter((s) => s.active), [sites]);

    return (
        <div className="space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">Admin</div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Operations Dashboard
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm">
                        {siteScoped
                            ? <>Showing metrics scoped to <span className="font-semibold text-[#003366]">{siteFilter}</span>.</>
                            : "Monitor your team, payments, and incoming requests at a glance."}
                    </p>
                </div>
                <div className="flex items-center gap-2 sm:min-w-[260px]">
                    <Buildings size={18} className="text-slate-400 shrink-0" />
                    <Select value={siteFilter} onValueChange={setSiteFilter}>
                        <SelectTrigger className="h-10 flex-1" data-testid="dashboard-site-filter">
                            <SelectValue placeholder="All sites" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All sites</SelectItem>
                            {activeSites.map((s) => (
                                <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {siteScoped && (
                <div className="flex items-center gap-2" data-testid="dashboard-site-banner">
                    <Badge className="bg-[#0066D9] hover:bg-[#0066D9] text-white text-[10px] uppercase tracking-wider">
                        Site filter active
                    </Badge>
                    <button
                        onClick={() => setSiteFilter("all")}
                        className="text-xs font-semibold text-[#0066D9] hover:underline"
                        data-testid="dashboard-clear-site-filter"
                    >
                        Clear filter
                    </button>
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-32 rounded-md" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard
                        label={siteScoped ? "Technicians at Site" : "Active Technicians"}
                        value={stats?.total_technicians ?? 0}
                        icon={Users}
                        accent="bg-[#003366]"
                    />
                    <MetricCard
                        label="Pending Approvals"
                        value={stats?.pending_approvals ?? 0}
                        icon={UserCheck}
                        accent="bg-amber-500"
                        sub="Action needed"
                        globalScope={siteScoped}
                    />
                    <MetricCard
                        label="Open Requests"
                        value={(stats?.pending_requests ?? 0) + (stats?.in_review_requests ?? 0)}
                        icon={ChatCircleDots}
                        accent="bg-[#E6002C]"
                        globalScope={siteScoped}
                    />
                    <MetricCard
                        label="Total Paid"
                        value={`$${Number(stats?.total_paid ?? 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                        icon={Wallet}
                        accent="bg-[#0066D9]"
                        sub={`${stats?.total_reports ?? 0} reports`}
                    />
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Pending approvals */}
                <Card className="border-[#E2E8F0]">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                                    Action queue
                                </div>
                                <h3 className="text-lg font-semibold text-[#1F2A44]" style={{ fontFamily: "Outfit" }}>
                                    Pending account approvals
                                </h3>
                            </div>
                            <Link to="/admin/users">
                                <Button variant="ghost" size="sm" className="text-[#0066D9] hover:bg-blue-50">
                                    View all <ArrowRight size={14} className="ml-1" />
                                </Button>
                            </Link>
                        </div>
                        {pendingUsers.length === 0 ? (
                            <div className="text-sm text-slate-500 py-6">No pending approvals — you're all caught up.</div>
                        ) : (
                            <div className="divide-y divide-[#E2E8F0]">
                                {pendingUsers.slice(0, 5).map((u) => (
                                    <div
                                        key={u.id}
                                        className="flex items-center justify-between py-3"
                                        data-testid={`pending-user-${u.id}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-[#1F2A44] truncate">
                                                {u.full_name}
                                            </div>
                                            <div className="text-xs text-slate-500 truncate">
                                                {u.email} · {u.position || "—"}
                                            </div>
                                        </div>
                                        <StatusBadge status="pending" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent requests */}
                <Card className="border-[#E2E8F0]">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                                    Activity
                                </div>
                                <h3 className="text-lg font-semibold text-[#1F2A44]" style={{ fontFamily: "Outfit" }}>
                                    Latest pending requests
                                </h3>
                            </div>
                            <Link to="/admin/requests">
                                <Button variant="ghost" size="sm" className="text-[#0066D9] hover:bg-blue-50">
                                    View all <ArrowRight size={14} className="ml-1" />
                                </Button>
                            </Link>
                        </div>
                        {recentRequests.length === 0 ? (
                            <div className="text-sm text-slate-500 py-6">No pending requests.</div>
                        ) : (
                            <div className="divide-y divide-[#E2E8F0]">
                                {recentRequests.slice(0, 5).map((r) => (
                                    <div
                                        key={r.id}
                                        className="flex items-center justify-between py-3"
                                        data-testid={`pending-request-${r.id}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-[#1F2A44] truncate">{r.subject}</div>
                                            <div className="text-xs text-slate-500 truncate">
                                                {r.technician_name} · {r.type.replace(/_/g, " ")}
                                            </div>
                                        </div>
                                        <StatusBadge status={r.status} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <BirthdaysCard />
                <TopStreaksPreview />
            </div>

            {/* CTA bar */}
            <Card className="border-[#E2E8F0] bg-[#003366] text-white">
                <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-md bg-[#0066D9] flex items-center justify-center shrink-0">
                            <Lightning size={22} weight="fill" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
                                Need to create a weekly payment?
                            </h3>
                            <p className="text-sm text-white/80 mt-0.5">
                                Generate new payment reports for any technician in seconds.
                            </p>
                        </div>
                    </div>
                    <Link to="/admin/reports">
                        <Button className="bg-[#0066D9] hover:bg-[#0052ad] text-white" data-testid="cta-new-report">
                            <Receipt size={16} className="mr-1.5" /> Manage reports
                        </Button>
                    </Link>
                </CardContent>
            </Card>
        </div>
    );
}
